import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", email: "", company: "", phone: "", notes: "" };

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function add(e) {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email required");
      return;
    }
    try {
      await api.post("/admin/clients", form);
      toast.success("Client added");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function del(id) {
    if (!window.confirm("Delete this client?")) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="overline">Console · Clients</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl tracking-tighter">Clients · {clients.length}</h1>
          <p className="mt-1 text-neutral-400 text-sm">Advertisers, agencies and partners. Phase 2 will allow them to log in.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn-primary text-sm">
          <Plus size={14} /> {showForm ? "Close" : "Add client"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} className="mt-6 surface p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Name *", "name"], ["Email *", "email"], ["Company", "company"], ["Phone", "phone"],
          ].map(([label, key]) => (
            <label key={key} className="block">
              <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
              <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
            </label>
          ))}
          <label className="block md:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Notes</div>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
          <div className="md:col-span-2">
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="mt-8 text-neutral-500">Loading…</div>
      ) : clients.length === 0 ? (
        <div className="mt-8 surface p-10 text-center text-neutral-500">
          No clients yet. Add your first client above.
        </div>
      ) : (
        <div className="mt-8 surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-left text-[11px] uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="p-4">Name</th>
                <th className="p-4">Email</th>
                <th className="p-4">Company</th>
                <th className="p-4">Phone</th>
                <th className="p-4">Notes</th>
                <th className="p-4 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4 text-neutral-300">{c.email}</td>
                  <td className="p-4 text-neutral-400">{c.company || "—"}</td>
                  <td className="p-4 text-neutral-400">{c.phone || "—"}</td>
                  <td className="p-4 text-neutral-400 max-w-xs truncate">{c.notes || "—"}</td>
                  <td className="p-4">
                    <button onClick={() => del(c.id)} className="text-neutral-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
